import type {
  DrillLocation,
  FocusedActivationKey,
  ExplorationVolume,
  ViewMode,
} from '../../types/exploration';
import { reasonLabelForVolume } from '../sidebar/focusedReasonHelpers';
import { volumeStatusLabel } from '../sidebar/volumeStatusLabel';

export function volumeAccountKey(v: ExplorationVolume): string {
  return v.accountName || v.accountId || '(unknown account)';
}

export function withAccountPrefix(
  path: string[],
  volume: ExplorationVolume,
  groupByAccount: boolean
): string[] {
  return groupByAccount ? [volumeAccountKey(volume), ...path] : path;
}

export function withAccountPrefixForAnchor(
  path: string[],
  anchor: ExplorationVolume,
  groupByAccount: boolean
): string[] {
  return groupByAccount ? [volumeAccountKey(anchor), ...path] : path;
}

/**
 * True when the current location is already scoped to this volume's exact
 * instance — i.e. the instance was actually passed through to get here
 * (drilled into directly, or via SELECT_INSTANCE/InstanceDetails), as
 * opposed to the volume merely belonging to it. Selecting a volume should
 * only carry its instance into the resolved location in that case;
 * otherwise it belongs in its top-level/aggregate group instead.
 */
export function volumePassedThroughInstance(
  currentLocation: DrillLocation | undefined,
  volume: Pick<ExplorationVolume, 'instanceId'>
): boolean {
  if (!volume.instanceId) return false;
  const s = currentLocation?.structural;
  return !!s && 'instanceId' in s && s.instanceId === volume.instanceId;
}

export function isFocusableActivation(
  view: string,
  status: string
): status is FocusedActivationKey {
  return view === 'activation' && (status === 'unsupported' || status === 'potentiallySupported');
}

/**
 * Shared tail of every grouping model's locationForVolume: overlay either
 * the focused-activation reason bucket (Pending setup, in activation view)
 * or the plain status label onto a base {account, structural} location.
 * Unsupported has no reason breakdown anywhere else in the app, so its
 * canonical location stops at focusedActivationKey — setting reasonLabel
 * too would make a volume's own anchor location narrower than the
 * Unsupported-category view the user is already browsing, causing
 * selection to silently re-scope drillPath away from it.
 */
export function withVolumeStatusOverlay(
  base: Pick<DrillLocation, 'account' | 'structural'>,
  volume: ExplorationVolume,
  view: ViewMode
): DrillLocation {
  if (isFocusableActivation(view, volume.activationStatus)) {
    if (volume.activationStatus === 'unsupported') {
      return { ...base, focusedActivationKey: 'unsupported' };
    }
    return {
      ...base,
      focusedActivationKey: volume.activationStatus,
      reasonLabel: reasonLabelForVolume(volume, volume.activationStatus),
    };
  }
  return { ...base, statusLabel: volumeStatusLabel(volume, view) };
}
